import { create } from 'zustand'

/**
 * A plan Paddle has been paid for and the API has not confirmed yet.
 *
 * Paddle's checkout reports `checkout.completed` the moment it takes the card;
 * the plan becomes ours when its webhook reaches the API, a few seconds later.
 * The gap is visible wherever the plan is shown — the drawer's plan item, the
 * plan dialog, the billing page — and it can start on one page and end on
 * another: onboarding pays and then goes straight to the dashboard. So the
 * wait lives here rather than in whichever component opened the checkout, and
 * `PlanActivationWatcher` (mounted once, above the route tree) is what ends it.
 *
 * UI state only (frontend CLAUDE.md §4): the plan itself is fetched data and
 * stays in SWR. Reset from `clearSession()` — a module-level store outlives
 * sign-out, and the next person to sign in must not see "Activating Pro…".
 */

type PlanActivationState = {
    /** The plan code being waited for, or null. */
    pending: string | null
    /**
     * Waited out the watcher's patience without seeing it. Not a failure — the
     * hourly reconcile delivers it even if the webhook never does — but worth
     * saying, so nobody pays twice.
     */
    stalled: string | null
    start: (planCode: string) => void
    settle: () => void
    stall: () => void
    reset: () => void
}

export const usePlanActivationStore = create<PlanActivationState>((set) => ({
    pending: null,
    stalled: null,
    start: (planCode) => set({ pending: planCode, stalled: null }),
    settle: () => set({ pending: null, stalled: null }),
    stall: () => set((state) => ({ pending: null, stalled: state.pending })),
    reset: () => set({ pending: null, stalled: null }),
}))

/** Called from `clearSession()`. See the note above. */
export const resetPlanActivation = () =>
    usePlanActivationStore.getState().reset()
